import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, Typography, Box, IconButton, useTheme, Zoom, Autocomplete, TextField, CircularProgress, alpha
} from '@mui/material';
import { PersonSearch as PersonSearchIcon, Close as CloseIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useDebounce } from '../../shared/hooks';
import { ParceirosService } from '../../shared/services/api/Venda/ParceiroService';
import { PDVThemeColors } from './PDVThemes';

type TOpcaoParceiro = {
  idparceiro: string;
  label: string;
};

interface DialogSelecionarParceiroProps {
  open: boolean;
  onClose: () => void;
  onSelect: (idparceiro: string, nome: string) => void;
  colors: PDVThemeColors;
}

// Dialog independente do Form de pagamento — usado para trocar o cliente da
// venda a qualquer momento, não só no fluxo de parcelamento (que já tem seu
// próprio AutoCompleteParceiro preso ao formulário via unform).
export const DialogSelecionarParceiro: React.FC<DialogSelecionarParceiroProps> = ({ open, onClose, onSelect, colors }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

  const { debounce } = useDebounce();
  const [busca, setBusca] = useState('');
  const [opcoes, setOpcoes] = useState<TOpcaoParceiro[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selecionado, setSelecionado] = useState<TOpcaoParceiro | null>(null);

  useEffect(() => {
    if (open) {
      setBusca('');
      setOpcoes([]);
      setSelecionado(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    setIsLoading(true);
    debounce(() => {
      ParceirosService.getAllList(busca)
        .then((resultado) => {
          setIsLoading(false);
          if (resultado instanceof Error) return;
          setOpcoes(resultado.data.map((parceiro) => ({
            idparceiro: parceiro.idparceiro,
            label: `${parceiro.nome} - ${parceiro.cpf}`,
          })));
        });
    });
  }, [busca, open, debounce]);

  const confirmar = () => {
    if (!selecionado) return;
    onSelect(selecionado.idparceiro, selecionado.label.split(' - ')[0]);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      TransitionComponent={Zoom}
      TransitionProps={{ timeout: 220 }}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `0.5px solid ${borderColor}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '8px',
              background: colors.primaryGradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <PersonSearchIcon sx={{ fontSize: 18, color: colors.textOnPrimary }} />
          </Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Selecionar cliente
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2.5 }}>
        <Autocomplete
          openText="Abrir"
          closeText="Fechar"
          noOptionsText="Sem opções"
          loadingText="Carregando..."
          options={opcoes}
          loading={isLoading}
          value={selecionado}
          onInputChange={(_, novoValor) => setBusca(novoValor)}
          onChange={(_, novoValor) => setSelecionado(novoValor)}
          popupIcon={isLoading ? <CircularProgress size={22} /> : undefined}
          autoHighlight
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              size="small"
              label="Buscar cliente por nome ou CPF"
            />
          )}
        />
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
        <Button
          variant="contained"
          fullWidth
          disabled={!selecionado}
          onClick={confirmar}
          startIcon={<CheckCircleIcon />}
          sx={{
            borderRadius: '10px',
            py: 1,
            background: colors.primaryGradient,
            color: colors.textOnPrimary,
            '&:hover': { background: colors.hoverGradient },
          }}
        >
          Selecionar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
